
import { embedSession } from 'utils/embedSession'
import { useCallback } from 'react'
import { useSyncExternalStore } from 'react'
import { useUpdatePreferredWorkspace } from 'dataQueries'

/**
 * Records a workspace as the user's preferred one, so signing in on another browser or device lands on it.
 * This is the only writer of that preference, which keeps two rules in one place:
 *
 * - nothing is written while an embed session pins the workspace. The pin is session-scoped and
 *   deliberately never persisted (ISSUE-2889), so writing it here would do by the back door what that
 *   invariant forbids: leave the shop workspace as the broker's own landing workspace outside the embed;
 * - failures are swallowed by the mutation itself — losing a cross-device restore must never interrupt the
 *   action the user actually took.
 *
 * Callers decide *which* events count as the user choosing a workspace. Landing somewhere automatically —
 * the first-workspace fallback, or opening a shared property link — deliberately does not.
 */
export const useRememberWorkspace = () => {
  const embedWorkspaceId = useSyncExternalStore(embedSession.subscribe, embedSession.getWorkspaceId)
  const { mutate: persistPreferredWorkspace } = useUpdatePreferredWorkspace()

  return useCallback((workspaceId: string) => {
    if (embedWorkspaceId) return
    persistPreferredWorkspace({ workspaceId })
  }, [embedWorkspaceId, persistPreferredWorkspace])
}
